import React, { AnimationEvent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import { useForm } from 'react-hook-form';
import { Box, BoxProps, LinearProgress, MenuItem, TextField, TextFieldProps, Typography } from '@mui/material';
import {
  VolumeUp as AudioClipIcon,
} from '@mui/icons-material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWithActionBar from '../FormWithActionBar/FormWithActionBar';
import { ControlsStringAssets, KeyValuePairsStringAssets, MessagesStringAssets } from '../../../assets/stringAssets';
import { IAudioLink } from '../../../dataObjects/models/digitalMedia/AudioLink';
import { IFileUploadProgress } from '../../../dataObjects/models/fileUpload/FileUploadProgress';
import { typeUniqueId } from '../../../dataObjects/types';
import { enumFileUploadType, enumAudioLinkType, enumAudioLinkTypeConvert, enumDigitalMediaDisplayEnvironment } from '../../../dataObjects/enums';
import { ISaveAudioLinkRequest } from '../../../dataObjects/models/serviceRequests/audioLink/SaveAudioLinkRequest';
import { IFileUploadRequest } from '../../../dataObjects/models/fileUpload/FileUploadRequest';
import FileInput from '../../controls/FileInput/FileInput';
import { Accept, DropEvent, FileRejection } from 'react-dropzone';
import { FileForUpload, IFileForUpload } from '../../../dataObjects/models/fileUpload/FileForUpload';
import MediaFileUploadList from '../../listsAndListItems/mediaFiles/MediaFileUploadList';
import { enumAlertType } from '../../enums';
import { AlertInfo, IAlertInfo } from '../../../dataObjects/models/alerts/AlertInfo';
import { constructDropZoneFileRejectionsErrorMessage } from '../../utilities/constructDropZoneFileRejectionsErrorMessage';
import { styled } from '@mui/styles';
import { MdbError } from '../../../errorObjects/MdbError';
import { AudioMediaDisplay } from '../../controls/digitalMediaDisplays/AudioMediaDisplay';
import { useAppDispatch } from '../../../uiMiddleware-redux/store/configureStore';
import { alertInfoChange } from '../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';
import { audioLinkFileUploadProgressUpdate } from '../../../uiMiddleware-redux/slices/audioLink/audioLinkSaveStatusSlice';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';
import { urlIsAnAppleMusicAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/appleMusicUtilities';
import { referenceIsForAYouTubeAsset } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/youtubeUtilities';
import { urlIsAMixCloudAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/mixCloudUtilities';
import { urlIsASoundCloudAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/soundCloudUtilities';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled TextField that is serving as a Select control
const StyledTextFieldForSelectControl = styled((props: TextFieldProps) => (
  <TextField
    select
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

// a styled Box (equivalent to a <div>), providing an area to display the Preview Container
const StyledBoxForPreviewContainer = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '10000px', // to give it completely rounded sides
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
  height: '3rem',
  width: 'auto',
  transition: '0.5s ease-in',
}));

// // a styled Box (equivalent to a <div>), providing an area to display the Preview Item
// const StyledBoxForPreviewItemArea = styled((props: BoxProps) => (
//   <Box
//     {...props}
//   />
// ))(({ theme }) => ({
//   display: 'flex',
//   justifyContent: 'center',
// }));

// interface IAudioLinkFormValues {
//   audioLinkType: string;
//   audioUrl: string;
//   description: string;
// }

// using 'yup', set up a schema for the form field values
const schema = yup.object().shape({

  audioLinkType: yup
    .string()
    .required(),

  /**
   * @property {string} audioUrl A URL for the AudioLink IFF the type is NOT Audio Upload
   */
  audioUrl: yup
    .string(), // needs to be required if a Audio site is selected from the AudioLinkType dropdown
  // .required(),

  /**
   * @property {string} description A description for the AudioLink.
   */
  description: yup
    .string()
    .required(ControlsStringAssets.audioLinkDescriptionRequired),
});

export interface IAudioLinkFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {typeUniqueId} userId The id of the current user
   */
  userId: typeUniqueId,

  /**
   * @property {IAudioLink} audioLink The AudioLink details for the form (will have blank properties values if we're creating a new record)
   */
  audioLink: IAudioLink,

  /**
   * @property {boolean} saveRequestInProgress (optional) Whether a save request is in progress (default: false).
   */
  saveRequestInProgress?: boolean,

  /**
   * @property {string} progressMessage (optional) If a save is in progress, display this message to provide progress updates to the user.
   */
  progressMessage?: string,

  /**
   * @property {Array<IFileUploadProgress>} fileUploadProgressCollection (optional) If files are in the process of being uploaded, this array
   *                                        provides the details for the upload progress of each file being uploaded.
   */
  fileUploadProgressCollection?: Array<IFileUploadProgress>,

  /**
   * @property {(saveAudioLinkRequest: ISaveAudioLinkRequest) => void} onSubmit Method to call for submitting the form for a save operation
   */
  onSubmit: (saveAudioLinkRequest: ISaveAudioLinkRequest) => void,
}

const AudioLinkForm: React.FC<IAudioLinkFormProps> = (props: IAudioLinkFormProps) => {
  AudioLinkForm.displayName = 'AudioLink Form';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = true;

  const dispatch = useAppDispatch();

  // get required arguments from props
  const { userId, audioLink, progressMessage, onSubmit } = props;

  // set up details for ReactHookForm
  // const { register, formState, formState: { errors }, handleSubmit } = useForm<IAudioLinkFormValues>({
  const { control, formState: { errors }, getValues, handleSubmit, register } = useForm({
    defaultValues: {
      audioLinkType: audioLink.audioLinkType,
      audioUrl: audioLink.downloadUrl,
      description: audioLink.description
    },
    mode: "all",
    resolver: yupResolver(schema)
  });

  const { ref: audioLinkTypeReg } = register("audioLinkType", { required: true });
  const { ref: audioUrlReg, ...audioUrlProps } = register("audioUrl", { required: true });
  const { ref: descriptionReg, ...descriptionProps } = register("description", { required: true, minLength: 1 });

  // for convenience, use a state variable to indicate whether we are working with an existing AudioLink
  const [isExistingAudioLink, setIsExistingAudioLink] = useState<boolean>(false);

  // files selected by the user
  const [filesSelectedForUpload, setFilesSelectedForUpload] = useState<Array<IFileForUpload> | null>(null);

  const [audioLinkTypeKey, setAudioLinkTypeKey] = useState<string>("");

  const [audioUrlCurrentValue, setAudioUrlCurrentValue] = useState<string>(audioLink.downloadUrl);

  const filesAreSelectedForUpload: boolean = (filesSelectedForUpload !== null) ? filesSelectedForUpload.length > 0 : false;

  const [audioLinkTypesForDropdown, setAudioLinkTypesForDropdown] = useState<Array<React.JSX.Element>>([]);

  displayConsoleLogs && console.log(`In AudioLinkForm. audioLinkTypeKey: ${audioLinkTypeKey}`);

  // useEffect to be executed upon mounting of this component
  useEffect(() => {
    // prepare an array of AudioLinkType values from the enumAudioLinkType enumerator that can be used to populate MenuItem components for the AudioLinkType <Select> component
    let audioLinkTypeMenuItems: Array<React.JSX.Element> = [];
    KeyValuePairsStringAssets.audioLinkTypeValuePairs.forEach((keyValuePair: { key: string, value: string }) => {
      audioLinkTypeMenuItems.push(<MenuItem key={keyValuePair.key} value={keyValuePair.key}>{keyValuePair.value}</MenuItem>);
    });

    setAudioLinkTypesForDropdown(audioLinkTypeMenuItems);
  }, []);

  // execute whenever audioLink.audioLinkType changes
  useEffect(() => {
    // set the audioLinkTypeKey from the audioLink.audioLinkType value
    const defaultAudioLinkTypeKey: string = (audioLink.audioLinkType === undefined) ? enumAudioLinkType.GoogleCloudStorage : audioLink.audioLinkType;
    setAudioLinkTypeKey(defaultAudioLinkTypeKey);

  }, [audioLink.audioLinkType]);

  // // displayConsoleLogs && console.log(`formIsValid: ${formIsValid}; filesSelectedForUpload is ${filesSelectedForUpload === null ? 'null' : 'non-null'}`);
  // if (filesSelectedForUpload !== null) {
  //   // displayConsoleLogs && console.log(`filesSelectedForUpload.length: ${filesSelectedForUpload.length}`);
  // }

  // if uploading a file, the current progress
  const [fileUploadProgress, setFileUploadProgress] = useState<IFileUploadProgress | undefined>(undefined);

  // capture whether a save is currently being submitted
  const saveRequestInProgress: boolean = props.saveRequestInProgress ?? false;

  // state value indicating whether a save is in progress
  const [saveInProgress, setSaveInProgress] = useState<boolean>(saveRequestInProgress);

  // for capturing the current value of the Description field
  const [currentDescription, setCurrentDescription] = useState<string>(audioLink.description);

  const [isDescriptionAutoFilled, setIsDescriptionAutoFilled] = useState<boolean>(false);
  const [isAudioUrlAutoFilled, setIsAudioUrlAutoFilled] = useState<boolean>(false);

  const [formIsValid, setFormIsValid] = useState<boolean>(false);

  // determines whether the description field value is valid
  const validDescriptionFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentDescription !== undefined) {
        isFieldValueValid = currentDescription.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isDescriptionAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentDescription, isDescriptionAutoFilled]
  );

  // determines whether the audio link url (external url) field value is valid
  const validAudioUrlFieldValue: () => boolean = useCallback(
    () => {
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (audioUrlCurrentValue !== undefined) {
        isFieldValueValid = RegularExpressions.GenericUrl.test(audioUrlCurrentValue);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isAudioUrlAutoFilled;
      }

      return isFieldValueValid;
    },
    [audioUrlCurrentValue, isAudioUrlAutoFilled]
  );

  const allRequiredFieldValuesValid: () => boolean = useCallback(
    () => {
      let fieldValuesValid: boolean = false;

      displayConsoleLogs && console.log(`%c AudioLinkForm. In allRequiredFieldValuesValid(); currentDescription: ${currentDescription}; audioLinkTypeKey: ${audioLinkTypeKey}, isExistingAudioLink: ${isExistingAudioLink}, (filesSelectedForUpload non-null?) ${filesSelectedForUpload !== null}`, 'background: #055; color: #fff');

      // if the media provided is a supported media
      // if there's a valid description...
      if (validDescriptionFieldValue()) {
        // if the selection is for Google Cloud Storage (upload an audio)...
        if (audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage) {
          // if it's an existing audio link (meaning, we're updating the record)
          if (isExistingAudioLink) {
            fieldValuesValid = true;
          } else {
            // user must be attempting to upload a new audio, so if files are selected for upload and we have at least one file in the list...
            if (filesAreSelectedForUpload && (filesSelectedForUpload && filesSelectedForUpload.length > 0)) {
              fieldValuesValid = true;
            }
          }
        } else {
          if ((audioLinkTypeKey === enumAudioLinkType.ExternalSource) && (validAudioUrlFieldValue())) {
            if (urlIsAnAppleMusicAddress(audioUrlCurrentValue) ||
              referenceIsForAYouTubeAsset(audioUrlCurrentValue) ||
              urlIsAMixCloudAddress(audioUrlCurrentValue) ||
              urlIsASoundCloudAddress(audioUrlCurrentValue)) {

              fieldValuesValid = true;
            }
          }
        }
      }

      return fieldValuesValid;
    },
    [validDescriptionFieldValue, audioLinkTypeKey, filesAreSelectedForUpload, filesSelectedForUpload, isExistingAudioLink, audioUrlCurrentValue]
  );

  const onDescriptionAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c AudioLinkForm. In onDescriptionAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c AudioLinkForm. In onDescriptionAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isDescriptionAutoFilled) {
            setIsDescriptionAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c AudioLinkForm. In onDescriptionAnimationStart(); getValues('description'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  const onAudioUrlAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c AudioLinkForm. In onAudioUrlAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c AudioLinkForm. In onAudioUrlAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isAudioUrlAutoFilled) {
            setIsAudioUrlAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c AudioLinkForm. In onAudioUrlAnimationStart(); getValues('audioUrl'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  useEffect(() => {
    // let isFormValid: boolean = isValid && allRequiredFieldValuesValid();
    let isFormValid: boolean = allRequiredFieldValuesValid();

    displayConsoleLogs && console.log(`%c AudioLinkForm. useEffect for [allRequiredFieldValuesValid]; isFormValid: ${isFormValid}`, 'background: #055; color: #fff');

    setFormIsValid(isFormValid);
  }, [allRequiredFieldValuesValid]);


  // useEffect hook for setting the 'saveInProgress' local state based on whether a save is currently in progress
  useEffect(() => {
    setSaveInProgress(saveRequestInProgress);
  }, [saveRequestInProgress]);


  // if this is an existing audioLink (audioLink.downloadUrl is non-blank), execute logic based on the audioLink.baseStoragePath
  useEffect(() => {
    if (audioLink.downloadUrl && audioLink.baseStoragePath) {
      // set convenience state variable indicating that we're working with an existing AudioLink
      setIsExistingAudioLink(true);
    } else {
      // set convenience state variable indicating that we're NOT working with an existing AudioLink
      setIsExistingAudioLink(false);
    }
  }, [audioLink]);


  function handleFilesSelectedForUpload<T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent): void {
    displayConsoleLogs && console.log(`%c AudioLinkForm. In handleFilesSelectedForUpload()`, 'background: #055; color: #fff');

    let filesForUpload: Array<IFileForUpload> = new Array<IFileForUpload>();

    displayConsoleLogs && console.log(`audioLinkForm.handleFilesSelectedForUpload. acceptedFiles: ${JSON.stringify(acceptedFiles)}`);

    // capture any files accepted by the file input
    acceptedFiles.forEach(file => {
      filesForUpload.push(new FileForUpload(file, enumFileUploadType.AudioFiles));

      // clear any alerts
      dispatch(alertInfoChange(null));
    });

    displayConsoleLogs && console.log(`audioLinkForm.handleFilesSelectedForUpload. filesForUpload: ${JSON.stringify(filesForUpload)}`);
    displayConsoleLogs && console.log(`audioLinkForm.handleFilesSelectedForUpload. audioLinkTypeKey: ${JSON.stringify(audioLinkTypeKey)}`);

    switch (audioLinkTypeKey) {
      case enumAudioLinkType.GoogleCloudStorage:
        setFilesSelectedForUpload(filesForUpload);

        // if any files were rejected, notify the user with an alert
        if (fileRejections.length > 0) {
          const errorMessage = constructDropZoneFileRejectionsErrorMessage(fileRejections);

          // create an AlertInfo object and dispatch a request to set the AlertInfo into Redux state
          const alertInfo: IAlertInfo = new AlertInfo(true, enumAlertType.Error, errorMessage);
          dispatch(alertInfoChange(alertInfo));
        }
        break;

      case enumAudioLinkType.ExternalSource:
        if (filesForUpload.length === 1) {
          displayConsoleLogs && console.log(`audioLinkForm.handleFilesSelectedForUpload. filesForUpload[0]: ${JSON.stringify(filesForUpload[0])}`);
          setAudioUrlCurrentValue(filesForUpload[0].file.name);
        }
        break;

      default:
        throw new MdbError(`Unhandled audioLinkTypeKey in AudioLinkForm. key: ${audioLinkTypeKey}`);
    }

  }

  function onFileUploadProgressUpdate(latestFileUploadProgress: IFileUploadProgress): void {
    // alert('Updating the file upload progress');
    setFileUploadProgress(latestFileUploadProgress);

    dispatch(audioLinkFileUploadProgressUpdate(latestFileUploadProgress));
  }


  // handles a save/submit request from the form
  // const handleSaveSubmit = async (data: IAudioLinkFormValues) => {
  const handleSaveSubmit = async (data: any) => {

    setSaveInProgress(true);

    // fill in name & description of the AudioLink object passed in
    audioLink.description = data.description;
    audioLink.audioLinkType = enumAudioLinkTypeConvert.fromString(audioLinkTypeKey);

    // if the audio link is for an external source...
    if (audioLinkTypeKey === enumAudioLinkType.ExternalSource) {
      // set the downloadUrl
      audioLink.downloadUrl = data.audioUrl;
    }

    // prepare a request for saving, starting with just the audioLink
    let saveAudioLinkRequest: ISaveAudioLinkRequest = {
      audioLink: audioLink
    };

    // if this is a new object being saved, along with a file upload...
    if (!audioLink.downloadUrl) {

      // if there are files to be uploaded, proceed
      if (filesSelectedForUpload !== null && filesSelectedForUpload.length > 0) {
        // // set flag indicating that a file upload is in progress
        // setFileUploading(true);

        // prepare the details of the FileUploadRequest
        // for now, only upload the first file in the list
        const fileUploadRequest: IFileUploadRequest = {
          userId: userId,
          fileToUpload: filesSelectedForUpload[0].file,
          fileClass: filesSelectedForUpload[0].fileClass,
          fileUniqueId: filesSelectedForUpload[0].id,
          uploadProgressCallback: onFileUploadProgressUpdate
        };

        // add the FileUploadRequest to the save request object
        saveAudioLinkRequest.fileUploadRequest = fileUploadRequest;

      }
    }

    // call the onSubmit handler passed in, supplying the save request
    await onSubmit(saveAudioLinkRequest);
  }

  const handleDeleteFileForUpload: (fileForUpload: IFileForUpload) => void = (fileForUpload: IFileForUpload) => {
    if (filesSelectedForUpload && filesSelectedForUpload.length > 0) {
      // ...copy the filesSelectedForUpload array to a local array variable, and then remove the specified item from the new array
      let revisedFilesSelectedForUpload: Array<IFileForUpload> = [...filesSelectedForUpload];
      revisedFilesSelectedForUpload.splice(revisedFilesSelectedForUpload.findIndex(fileForUploadElement => fileForUploadElement.id === fileForUpload.id), 1);

      // now, set the revised array into state
      setFilesSelectedForUpload(revisedFilesSelectedForUpload);
    }
  }

  const fileUploadCompletePercent: string = (fileUploadProgress && fileUploadProgress.percentComplete) ?
    fileUploadProgress.percentComplete.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :
    '0';


  const [urlForFileSelectedForUpload, setUrlForFileSelectedForUpload] = useState<string | undefined>(undefined);
  // const [urlWithTimeOffsetForFileSelectedForUpload, setUrlWithTimeOffsetForFileSelectedForUpload] = useState<string | undefined>(undefined);

  // Anytime there's a change in values that pertain to whether there's a file selected for upload, establish the URL (or undefined) for a file upload
  useEffect(() => {
    let newUrlForFileSelectedForUpload: string | undefined = undefined;

    // if this is a new AudioLink (isn't an existing AudioLink) -AND- there's a file selected for upload, obtain a URL for the selected file to be uploaded
    // (Note: If it's either an existing Audio Link or there's no file selected for upload, the Url string will be set as undefined.)
    if (!isExistingAudioLink && filesSelectedForUpload && filesSelectedForUpload.length > 0) {
      newUrlForFileSelectedForUpload = URL.createObjectURL(filesSelectedForUpload[0].file);

      setUrlForFileSelectedForUpload(newUrlForFileSelectedForUpload);
    } else {
      setUrlForFileSelectedForUpload(undefined);
    }

  }, [isExistingAudioLink, filesSelectedForUpload, setUrlForFileSelectedForUpload])

  // memoize the rendering of the audio object for upload so that it won't flicker each time this form component is re-rendered (such as when file is being uploaded)
  const audioObjectForUploadOperation = useMemo(() => (
    <>
      {
        ((audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage) && (urlForFileSelectedForUpload !== undefined)) ?
          <StyledBoxForPreviewContainer>
            <ReactPlayer
              style={{ height: '3rem', position: 'relative' }}
              url={urlForFileSelectedForUpload}
              controls
              width='100%'
              height='100%'
            />
          </StyledBoxForPreviewContainer>
          :
          <>
          </>
      }
    </>
  ), [audioLinkTypeKey, urlForFileSelectedForUpload]);

  // memoize the rendering of the audio for existing AudioLink so that it won't flicker each time this form component is re-rendered (during update operation)
  const audioObjectForExistingLinkUpdate = useMemo(() => (
    <>
      {
        ((audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage) && isExistingAudioLink) ?
          <AudioMediaDisplay audioLink={audioLink} displayEnvironment={enumDigitalMediaDisplayEnvironment.FormView} />
          :
          <>
          </>
      }
    </>
  ), [audioLinkTypeKey, isExistingAudioLink, audioLink]);

  // prepare specs for accepted filetypes that will be passed on to the FileInput component
  const acceptedFileTypes: Accept = { 'audio/m4a': [], 'audio/mp3': [], 'audio/mp4': [], 'audio/mpeg': [], 'audio/oga': [], 'audio/ogg': [], 'audio/opus': [], 'audio/wav': [], 'audio/weba': [], 'audio/webm': [] };

  function handleAudioLinkTypeChange(newAudioLinkTypeKey: string) {
    setAudioLinkTypeKey(newAudioLinkTypeKey);
  }

  return (
    <>
      <FormWithActionBar
        onSubmit={handleSubmit(handleSaveSubmit)}
        actionInProgress={saveInProgress}
        actionInProgressLabel={progressMessage}
        finiteActionInProgressPercentComplete={fileUploadProgress && fileUploadProgress.percentComplete}
        finiteActionInProgressLabel={fileUploadProgress && MessagesStringAssets.file_Uploading}
        formIsValid={formIsValid}
      >

        {/* We use a TextField with 'select' attribute as a pseudo <Select> (or dropdown) control */}
        {/* Only display the field if the dropdown options have been created */}
        {(audioLinkTypesForDropdown.length > 0) &&
          <StyledTextFieldForSelectControl
            inputRef={audioLinkTypeReg}
            label="Audio Type"
            margin='normal'
            value={audioLinkTypeKey}
            onChange={e => handleAudioLinkTypeChange(e.target.value)}
          >
            {audioLinkTypesForDropdown}
          </StyledTextFieldForSelectControl>
        }

        {/* if the selected link type is "GoogleCloudStorage" -AND- we're not editing an existing audioLink -AND-
           no files have been selected for upload,  */}
        {(audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage) && !isExistingAudioLink && !filesAreSelectedForUpload &&
          <FileInput
            // control={control}
            name="fileInput"
            // Notes: 
            //   1) The acceptedFileTypes must include a fully itemized list of audio mime types; otherwise, when running on iOS (iPhone/iPad), 
            //      the react-dropzone component will disable the files such that they can't be selected.
            //   2) "audio/*" must be included in the list of acceptedFileTypes. If not, the react-dropzone component will reject any selected file.
            // acceptedFileTypes="audio/*,audio/m4a,audio/mp3,audio/mp4,audio/mpeg,audio/oga,audio/ogg,audio/opus,audio/wav,audio/weba,audio/webm"
            acceptedFileTypes={acceptedFileTypes}
            allowMultipleFiles={false}
            onFilesSelectedForUpload={handleFilesSelectedForUpload}
            promptNotDragging={ControlsStringAssets.audioDropZonePromptNotDragging}
            promptWhileDragging={ControlsStringAssets.audioDropZonePromptWhileDragging}
          >
          </FileInput>
        }

        {/* if the currently selected Audio Type is file upload (GoogleCloudStorage), we'll have one type of rendering for the 
            audio display and any potential file upload activity */}
        {
          audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage &&

          /* if we're not editing an existing audioLink and files have been selected for upload */
          (!isExistingAudioLink && filesSelectedForUpload && filesSelectedForUpload.length > 0) &&
          <>
            {
              audioObjectForUploadOperation
            }

            <MediaFileUploadList
              filesForUpload={filesSelectedForUpload}
              mediaIcon={<AudioClipIcon />}
              onDelete={handleDeleteFileForUpload}
            />

            {
              fileUploadProgress &&
              <>
                <Box display='flex' flexDirection='column' >
                  <Box display="flex" alignItems="center">
                    <Box
                      width="100%"
                      mr={1}>
                      <LinearProgress
                        variant="determinate"
                        value={fileUploadProgress.percentComplete}
                      />
                    </Box>
                    <Box minWidth={35}>
                      <Typography
                        variant="body2"
                        color="primary">
                        {/* {`${Math.round(fileUploadProgress.percentComplete)}%`} */}
                        {`${fileUploadCompletePercent}%`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display='flex' justifyContent='center'>
                    <Typography
                      variant="caption"
                      color="primary">
                      {`Uploading audio...`}
                    </Typography>
                  </Box>
                </Box>
              </>
            }

          </>
        }

        {/* if we're editing an existing AudioLink, display the audio object for the existing audio... */}
        {isExistingAudioLink &&
          audioObjectForExistingLinkUpdate
        }

        {/* if the selected Audio Link Type is NOT "Upload" (GoogleCloudStorage), show the Audio URL text box */}
        {(audioLinkTypeKey !== enumAudioLinkType.GoogleCloudStorage) &&
          <>
            <TextField
              inputRef={audioUrlReg}
              {...audioUrlProps}
              label="Audio URL"
              margin='normal'
              fullWidth
              error={!!errors.audioUrl}
              helperText={errors?.audioUrl?.message}
              slotProps={{
                inputLabel: {
                  required: true // this will cause an asterisk ('*') to appear at the end of the label text
                },
                input: {
                  onAnimationStart: onAudioUrlAnimationStart()
                }
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setAudioUrlCurrentValue(e.target.value);
              }}
            />

            <AudioMediaDisplay
              audioLink={{ ...audioLink, downloadUrl: audioUrlCurrentValue, description: currentDescription, audioLinkType: audioLinkTypeKey === enumAudioLinkType.GoogleCloudStorage ? enumAudioLinkType.GoogleCloudStorage : enumAudioLinkType.ExternalSource }}
              displayEnvironment={enumDigitalMediaDisplayEnvironment.FormView}
            />
          </>
        }

        <TextField
          inputRef={descriptionReg}
          {...descriptionProps}
          autoFocus
          label={ControlsStringAssets.topicDescriptionLabel}
          margin='normal'
          fullWidth
          multiline={true}
          minRows={3}
          maxRows={5}
          error={!!errors.description}
          helperText={errors?.description?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onDescriptionAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentDescription(e.target.value);
          }}
        />

      </FormWithActionBar>
    </>

  );
}

export default AudioLinkForm;